import { useState } from 'react'
import { useDispatch, useSelector } from 'react-redux'
import { defineMessages, useIntl } from 'react-intl'
import { Button, ButtonGroup, CurrencyInput, useToast } from '@homebotapp/hb-react-component-catalog'
import { useTrackingContext } from '../../../../providers/tracking'
import { NEW_STEPS } from '../../../../constants/client/onboarding'
import { OnboardingModalTitle } from './OnboardingModalTitle'
import { setPricePoint } from '../../../../actions/affordability'
import { TrackingEventConstants } from '../../../../constants/tracking'
import { StepContainer } from './StepContainer'
import { useUpdateClient } from '../../../../hooks/listings/useUpdateClient'
import { UPDATE_BUYER_INFO_SUCCESS } from '../../../../actions/actionTypes'
import { BuyerInfo } from '../../../../types/buyerInfo'
import { selectBuyerInfo } from '../../../../store/selectors/buyerInfo'

const MSG = defineMessages({
  skipForNow: {
    id: 'BuyerOnboarding.skipForNow',
    defaultMessage: 'Skip for now'
  },
  finish: {
    id: 'BuyerOnboarding.finish',
    defaultMessage: 'Finish'
  },
  next: {
    id: 'BuyerOnboarding.next',
    defaultMessage: 'Next'
  },
  successMessage: {
    id: 'BuyerOnboardingWizard.successMessage',
    defaultMessage: 'Search profile is 100% complete'
  }
})

interface EnterBudgetProps {
  getTotalStepsLeftToComplete: () => number
  handleNavigation: (saveCurrentStepData?: () => void) => void
  hasFavoriteMarkets: boolean
}

export const EnterBudget = ({
  getTotalStepsLeftToComplete,
  handleNavigation,
  hasFavoriteMarkets
}: EnterBudgetProps) => {
  const intl = useIntl()
  const dispatch = useDispatch()
  const toast = useToast()
  const { mutateAsync: updateClientInfo } = useUpdateClient()
  const { trackingClient } = useTrackingContext()
  const buyerInfo: BuyerInfo = useSelector(selectBuyerInfo)

  const [tuneValue, setTuneValue] = useState('')
  const useStep = 10000

  const handleSkipForNow = () => {
    handleNavigation()
  }

  const handleNext = () => {
    if (!tuneValue) return

    handleNavigation(async () => {
      const roundInputValue = price => Math.round(price / useStep) * useStep
      // round value to the nearest 1000
      const roundedValue = roundInputValue(Number(tuneValue))

      trackingClient?.clientCustomized({
        customization_type: TrackingEventConstants.CLIENT_UPDATED_PRICE_POINT
      })
      trackingClient?.buyerOnboarded({ price_point_set: Number(roundedValue) })
      dispatch(setPricePoint(roundedValue))

      const result = await updateClientInfo({
        input: {
          affordability: roundedValue
        }
      })
      if (result.updateClient.updatedAt) {
        dispatch({
          type: UPDATE_BUYER_INFO_SUCCESS,
          data: {
            ...buyerInfo,
            defaultAffordability: false,
            affordability: roundedValue
          }
        })
      }
    })
    if (getTotalStepsLeftToComplete() === 1 && hasFavoriteMarkets) {
      toast({
        title: intl.formatMessage(MSG.successMessage),
        status: 'success'
      })
    }
  }

  return (
    <StepContainer data-qa='confirmation'>
      <OnboardingModalTitle currentStep={NEW_STEPS.BUYER_ONBOARDED} />
      {/* @ts-ignore */}
      <CurrencyInput onChange={v => setTuneValue(v)} name='set-budget-dollars' />
      <ButtonGroup justifyContent='end' mt={3}>
        <Button
          tracking={{
            guid: 'kRzoMddxc3pcJPLPdVsAwa',
            ui_context: TrackingEventConstants.BUYING_ONBOARDING_WIZARD_SKIP_FOR_NOW,
            descriptor: MSG.skipForNow.defaultMessage
          }}
          onClick={handleSkipForNow}
        >
          {intl.formatMessage(MSG.skipForNow)}
        </Button>
        <Button isDisabled={!tuneValue} colorScheme='primary' onClick={handleNext}>
          {getTotalStepsLeftToComplete() === 1 ? intl.formatMessage(MSG.finish) : intl.formatMessage(MSG.next)}
        </Button>
      </ButtonGroup>
    </StepContainer>
  )
}
